import { Section, displayTitle, ringedBand } from "@/features/portal/components/portal-ui";
import { StoreButtons } from "@/features/portal/components/store-buttons";
import { upcomingDays } from "@/features/portal/utils/upcoming-days";
import { cn } from "@/lib/utils";
import { m } from "@/paraglide/messages.js";

const tags = {
  coach: "bg-primary text-primary-foreground",
  plan: "bg-primary/15 text-[var(--site-ink)]",
  diet: "bg-muted text-muted-foreground",
};

/** A sample week as the app's calendar lists it: sessions, a plan day and a diet day. */
const agenda = () => {
  const [first, second, third, fourth] = upcomingDays(4);
  const session = {
    title: m.portal_week_session_title(),
    detail: m.portal_week_session_detail(),
    kind: "coach" as const,
    tag: m.portal_week_tag_coach(),
  };
  return [
    { ...session, day: first },
    {
      day: second,
      title: m.portal_step_train_card_title(),
      detail: m.portal_step_train_card_detail(),
      kind: "plan" as const,
      tag: m.portal_week_tag_plan(),
    },
    {
      day: third,
      title: m.portal_week_diet_title(),
      detail: m.portal_week_diet_detail(),
      kind: "diet" as const,
      tag: m.portal_week_tag_diet(),
    },
    { ...session, day: fourth },
  ];
};

/**
 * The loud band mid-page: the whole week of training in the app, with the stores to get it
 * from. Solid primary, so it reads as the place the page turns from "what" to "get it".
 */
export const WeekBand = () => (
  <Section id="app">
    <div className={cn(ringedBand, "grid items-center gap-10 p-6 md:p-14 lg:grid-cols-2 lg:gap-12")}>
      <div>
        <h2 className={displayTitle}>
          {m.portal_week_title_lead()}
          <br />
          <span className="opacity-55">{m.portal_week_title_tail()}</span>
        </h2>
        <p className="mt-5 max-w-[440px] text-lg leading-[1.55] opacity-75">{m.portal_week_body()}</p>
        <StoreButtons tone="ink" className="mt-8" />
      </div>

      <div className="grid gap-2 rounded-3xl bg-background p-5 text-foreground shadow-[0_40px_80px_-30px_rgb(0_0_0/0.5)]">
        <div className="flex items-baseline justify-between px-1.5 pt-1 pb-2.5">
          <b className="text-lg font-semibold tracking-[-0.02em]">{m.portal_week_agenda()}</b>
        </div>
        {agenda().map((entry) => (
          <div
            key={`${entry.day?.key}-${entry.kind}`}
            className="grid grid-cols-[54px_1fr_auto] items-center gap-3 rounded-2xl bg-[var(--site-well)] p-3"
          >
            <span className="text-xs leading-tight text-muted-foreground">
              {entry.day?.short}
              <b className="block text-xl font-semibold text-foreground">{entry.day?.number}</b>
            </span>
            <span className="min-w-0">
              <strong className="block truncate text-[15px] font-semibold">{entry.title}</strong>
              <small className="block truncate text-[13px] text-muted-foreground">{entry.detail}</small>
            </span>
            <span
              className={cn(
                "rounded-full px-2.5 py-1 text-[11px] font-bold tracking-[0.06em] uppercase",
                tags[entry.kind],
              )}
            >
              {entry.tag}
            </span>
          </div>
        ))}
      </div>
    </div>
  </Section>
);
